import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { Route as RootRoute } from 'routes/__root'
import {
  globalSearchParamsSchema,
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { z } from 'zod'

export type OrgSiteIdsPair = {
  organizationId: string | undefined
  siteId: string | undefined
}

/**
 * Hook that provides a stable interface for reading and updating the selected organization
 * and site (the "org/site source of truth").
 *
 * The current implementation uses TanStack Router (search params) under the hood.
 *
 * @returns An object with getters (`getOrganizationId`, `getSiteId`) and a setter
 *   (`setOrgAndSiteIds`) for the current org and site.
 */
export function useSelectedOrgAndSiteInterface() {
  const { useSearch } = RootRoute
  const searchParams = useSearch()
  const navigate = useNavigate()

  /**
   * ---------------------------------------------------------------------------
   *                                Getters
   * ---------------------------------------------------------------------------
   */

  /**
   * Returns the currently selected organization ID from the source of truth.
   */
  const getOrganizationId = useCallback(() => {
    return searchParams[SEARCH_PARAM_ORG_ID]
  }, [searchParams])

  /**
   * Returns the currently selected site ID from the source of truth.
   */
  const getSiteId = useCallback(() => {
    return searchParams[SEARCH_PARAM_SITE_ID]
  }, [searchParams])

  /**
   * ---------------------------------------------------------------------------
   *                                Setters
   * ---------------------------------------------------------------------------
   */

  /**
   * Updates the selected organization and/or site in the source of truth.
   *
   * **Important:** You should `await` this call, as performing any subsequent
   * Tanstack navigations can result in a race condition where the org-site search-param updates
   * are discarded.
   *
   * @param input - Either a new `OrgSiteIdsPair` or a function that receives the previous
   *   pair and returns the next one.
   * @param commitNavigationToBrowserHistory - If `true` (default), the update creates a new
   *   history entry; if `false`, the current entry is replaced (e.g., to avoid adding a "back" stop to the history).
   * @returns A promise that resolves when the org and site updates have been committed (and it's safe to do another `navigate`).
   */
  const setOrgAndSiteIds = useCallback(
    async (
      input: ((prev: OrgSiteIdsPair) => OrgSiteIdsPair) | OrgSiteIdsPair,
      commitNavigationToBrowserHistory: boolean = true
    ) => {
      return navigate({
        to: '.',
        search: (
          prev: Record<string, unknown> &
            z.infer<typeof globalSearchParamsSchema>
        ) => {
          const valuesToUse: OrgSiteIdsPair =
            typeof input === 'function'
              ? input({
                  organizationId: prev[SEARCH_PARAM_ORG_ID],
                  siteId: prev[SEARCH_PARAM_SITE_ID],
                })
              : input

          return {
            ...prev,
            [SEARCH_PARAM_ORG_ID]: valuesToUse.organizationId,
            [SEARCH_PARAM_SITE_ID]: valuesToUse.siteId,
          }
        },
        replace: !commitNavigationToBrowserHistory,
      })
    },
    [navigate]
  )

  /**
   * ---------------------------------------------------------------------------
   *                                Return
   * ---------------------------------------------------------------------------
   */

  return {
    getOrganizationId,
    getSiteId,
    setOrgAndSiteIds,
  }
}
